import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Image as ImageIcon, Upload, Loader2, RefreshCw } from "lucide-react";

export default function CourierTatManager() {
  const [couriers, setCouriers] = useState([]);
  const [cid, setCid] = useState("");
  const [zones, setZones] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState("");
  const imgRef = useRef();
  const sheetRef = useRef();

  const loadCouriers = () => api.get("/couriers").then((r) => setCouriers(r.data)).catch(() => {});
  useEffect(() => { loadCouriers(); }, []);

  useEffect(() => {
    if (!cid) { setZones([]); setSummary(null); return; }
    const c = couriers.find((x) => x.id === cid);
    setZones((c?.zones || []).map((z) => ({ ...z })));
    api.get(`/couriers/${cid}/zone-pincodes/summary`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, [cid, couriers]);

  const setZone = (i, k, v) => setZones((z) => z.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const addZone = () => setZones((z) => [...z, { zone: "", days: "" }]);
  const delZone = (i) => setZones((z) => z.filter((_, idx) => idx !== i));

  const saveZones = async () => {
    setBusy("zones");
    try {
      const r = await api.put(`/couriers/${cid}/zones`, { zones: zones.map((z) => ({ zone: z.zone, days: Number(z.days) })) });
      toast.success(`Zones saved — ${r.data?.shipments_updated ?? 0} shipment(s) recalculated`);
      loadCouriers();
    } catch { toast.error("Failed to save zones"); }
    finally { setBusy(""); }
  };

  const onImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy("ocr");
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post(`/couriers/${cid}/zones/ocr`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const z = r.data?.zones || [];
      if (z.length) { setZones(z.map((x) => ({ ...x }))); toast.success(`Read ${z.length} zone(s) — review & Save`); }
      else toast.error("Could not read a zone table from that image");
    } catch { toast.error("OCR failed"); }
    finally { setBusy(""); if (imgRef.current) imgRef.current.value = ""; }
  };

  const onSheet = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy("sheet");
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post(`/couriers/${cid}/zone-pincodes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSummary({ origin: r.data.origin, zones: r.data.zones, total: r.data.total });
      toast.success(`Imported ${r.data.total} PIN→zone rows — recalculating overdue in the background`);
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setBusy(""); if (sheetRef.current) sheetRef.current.value = ""; }
  };

  const rebuildAll = async () => {
    setBusy("rebuild");
    try {
      const r = await api.post("/shipments/rebuild-tat");
      toast.success("Recalculating overdue for all shipments in the background");
    } catch { toast.error("Failed to recalculate"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="courier-tat-manager">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="min-w-[220px]">
          <Label>Courier</Label>
          <Select value={cid} onValueChange={setCid}>
            <SelectTrigger data-testid="tat-courier-select" className="mt-1.5"><SelectValue placeholder="Select a courier" /></SelectTrigger>
            <SelectContent>
              {couriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={rebuildAll} disabled={busy === "rebuild"} data-testid="tat-rebuild-all" className="gap-2">
          {busy === "rebuild" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Recalculate all overdue
        </Button>
      </div>

      {cid && (
        <div className="space-y-4">
          <div className="rounded-md border border-border bg-[#F9F9F8] p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm font-semibold text-[#111411]">Zone → delivery days</p>
              <div className="flex items-center gap-2">
                <input ref={imgRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onImage} data-testid="tat-ocr-input" />
                <Button variant="outline" size="sm" onClick={() => imgRef.current?.click()} disabled={busy === "ocr"} data-testid="tat-ocr-btn" className="gap-2">
                  {busy === "ocr" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} Read from image
                </Button>
              </div>
            </div>
            {zones.length === 0 && <p className="text-xs text-muted-foreground">No zones yet. Add rows or read them from a TAT image.</p>}
            {zones.map((z, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`tat-zone-row-${i}`}>
                <Input value={z.zone} onChange={(e) => setZone(i, "zone", e.target.value)} placeholder="Zone (e.g. Local)" className="h-9 flex-1" data-testid={`tat-zone-name-${i}`} />
                <Input value={z.days} onChange={(e) => setZone(i, "days", e.target.value)} placeholder="Days" type="number" className="h-9 w-24" data-testid={`tat-zone-days-${i}`} />
                <button onClick={() => delZone(i)} className="text-[#C24127] px-1" data-testid={`tat-zone-del-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={addZone} className="gap-1" data-testid="tat-zone-add"><Plus className="h-4 w-4" /> Add zone</Button>
              <Button size="sm" onClick={saveZones} disabled={busy === "zones"} data-testid="tat-zone-save" className="bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white gap-2">
                {busy === "zones" && <Loader2 className="h-4 w-4 animate-spin" />} Save zones
              </Button>
            </div>
          </div>

          <div className="rounded-md border border-border bg-white p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-sm font-semibold text-[#111411]">PIN → zone mapping</p>
                <p className="text-xs text-muted-foreground">Excel/CSV with columns <code className="bg-[#F4F6F4] px-1 rounded">pincode</code>, <code className="bg-[#F4F6F4] px-1 rounded">zone</code>. Replaces the current list.</p>
              </div>
              <input ref={sheetRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onSheet} data-testid="tat-sheet-input" />
              <Button variant="outline" size="sm" onClick={() => sheetRef.current?.click()} disabled={busy === "sheet"} data-testid="tat-sheet-btn" className="gap-2">
                {busy === "sheet" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload PIN→zone sheet
              </Button>
            </div>
            {summary && summary.total > 0 ? (
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="text-muted-foreground">{summary.total} PINs mapped (origin {summary.origin || "—"}):</span>
                {summary.zones.map((z) => (
                  <span key={z.zone} className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5">
                    {z.zone}: <b>{z.count}</b>
                  </span>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground">No PIN→zone data uploaded yet for this courier.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
